import axios from "../../axios";
import type { AxiosProgressEvent } from 'axios';


const URL = "/v2/program-reports";

// Get all program reports with pagination and filters
export const getProgramReports = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  userId?: number;
  companyName?: string;
  search?: string;
}) => {
  const response = await axios.get(URL, { params });
  return response.data;
};

// Get a specific program report by ID
export const getProgramReportById = async (id: string) => {
  const response = await axios.get(`${URL}/${id}`);
  return response.data;
};

// Create a new program report
export const createProgramReport = async (reportData: any) => {
  const response = await axios.post(URL, reportData);
  return response.data;
};

// Helper function to compress data before sending to backend without losing finding fields
const compressReportData = (reportData: any) => {
  const compressed = { ...reportData };

  // Ensure detailed_findings retains all fields (cvss_score, cvss_vector, owasp_category, target_ids, endpoint_values, etc.)
  if (compressed.detailed_findings && Array.isArray(compressed.detailed_findings)) {
    compressed.detailed_findings = compressed.detailed_findings.map((finding: any) => {
      const copy = { ...finding };
      delete copy.processedEvidence;
      delete copy.pieChartImage;
      delete copy.barChartImage;
      return copy;
    });
  }

  // Remove temporary UI state or heavy processed cache data
  delete compressed.processedFindings;
  delete compressed.pieChartImage;
  delete compressed.barChartImage;

  // Remove any large base64 images that might be embedded
  if (compressed.branding_logo && typeof compressed.branding_logo === 'string' && compressed.branding_logo.startsWith('data:image')) {
    console.warn('⚠️ Removing base64 branding_logo to reduce payload size');
    delete compressed.branding_logo;
  }

  return compressed;
};

// Update a program report
export const updateProgramReport = async (id: string, reportData: any) => {
  console.log('📦 Original payload size:', JSON.stringify(reportData).length, 'bytes');

  // Compress the data before sending
  const compressedData = compressReportData(reportData);

  console.log('📦 Compressed payload size:', JSON.stringify(compressedData).length, 'bytes');
  console.log('💾 Size reduction:',
    ((1 - JSON.stringify(compressedData).length / JSON.stringify(reportData).length) * 100).toFixed(2) + '%'
  );

  const response = await axios.put(`${URL}/${id}`, compressedData);
  return response.data;
};

// Update report status
export const updateReportStatus = async (id: string, status: string) => {
  const response = await axios.patch(`${URL}/${id}/status`, { status });
  return response.data;
};

// Delete a program report
export const deleteProgramReport = async (id: string) => {
  const response = await axios.delete(`${URL}/${id}`);
  return response.data;
};

// Get reports by automated report ID
export const getReportsByAutomatedId = async (automatedReportId: string) => {
  const response = await axios.get(`${URL}/automated/${automatedReportId}`);
  return response.data;
};

// Get report statistics
export const getReportStatistics = async () => {
  const response = await axios.get(`${URL}/statistics`);
  return response.data;
};

// Create report from automated report data
export const createFromAutomatedReport = async (automatedReportId: string, reportData: any) => {
  const response = await axios.post(`${URL}/from-automated/${automatedReportId}`, reportData);
  return response.data;
};

// Get reports that need review
export const getReportsForReview = async () => {
  const response = await axios.get(`${URL}/for-review`);
  return response.data;
};

// Bulk update report statuses
export const bulkUpdateReportStatus = async (reportIds: string[], status: string) => {
  const response = await axios.patch(`${URL}/bulk-status`, { reportIds, status });
  return response.data;
};

// Get program report preview
export const getProgramReportPreview = async (id: string, reportData: any) => {
  const response = await axios.post(
    `${URL}/${id}/preview`,
    { reportData },
    {
      headers: { 'Accept': 'text/html' },
      responseType: 'text'
    }
  );
  return response.data;
};

// Fetch change log for a program report
export const getProgramReportChangeLog = async (id: string) => {
  const response = await axios.get(`/v2/program-reports/${id}/change-log`);
  return response.data;
};

// Upload a frontend-generated PDF to Vanta via backend
export const uploadProgramReportPDFToVanta = async (
  id: string,
  file: Blob,
  onProgress?: (percent: number) => void
) => {
  const formData = new FormData();
  formData.append('file', file, 'report.pdf');
  const response = await axios.post(`${URL}/${id}/vanta/upload-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (pe) => {
      if (pe && pe.total) {
        const pct = Math.round((pe.loaded * 100) / pe.total);
        onProgress?.(pct);
      }
    }
  });
  return response.data;
};

// Delete a previously uploaded Vanta file by uploadId
export const deleteProgramReportVantaUpload = async (
  id: string,
  uploadId: string
) => {
  const response = await axios.delete(`${URL}/${id}/vanta/uploads/${uploadId}`);
  return response.data;
};
